import {
  CommercialMeterKey,
  CommercialPlanKey,
  type CommercialAllowances,
  type CommercialPlanPresentationDefinition,
} from "./types";

export const COMMERCIAL_CATALOG_GENERATION = "mem-tiered-2026-08-v1";

const buildAllowances = ({
  userRequests,
  newNotes,
  activeNotes,
  openTasksProjects,
  activeCustomRoutines,
  routineExecutions,
  activeConnections,
  voiceModeHours,
}: {
  userRequests: number;
  newNotes: number;
  activeNotes: number;
  openTasksProjects: number;
  activeCustomRoutines: number;
  routineExecutions: number;
  activeConnections: number;
  voiceModeHours: number;
}): CommercialAllowances => ({
  [CommercialMeterKey.UserRequests]: userRequests,
  [CommercialMeterKey.NewNotes]: newNotes,
  [CommercialMeterKey.ActiveNotes]: activeNotes,
  [CommercialMeterKey.OpenTasksProjects]: openTasksProjects,
  [CommercialMeterKey.ActiveCustomRoutines]: activeCustomRoutines,
  [CommercialMeterKey.RoutineExecutions]: routineExecutions,
  [CommercialMeterKey.ActiveConnections]: activeConnections,
  [CommercialMeterKey.VoiceModeHours]: voiceModeHours,
});

export const COMMERCIAL_PLAN_CATALOG: readonly CommercialPlanPresentationDefinition[] = [
  {
    key: CommercialPlanKey.Mem,
    displayName: "Mem",
    shortName: "Mem",
    monthlyPriceCents: 0,
    position: 0,
    supportsPaidExtraUsage: false,
    allowances: buildAllowances({
      userRequests: 25,
      newNotes: 25,
      activeNotes: 25,
      openTasksProjects: 15,
      activeCustomRoutines: 0,
      routineExecutions: 0,
      activeConnections: 0,
      voiceModeHours: 3,
    }),
  },
  {
    key: CommercialPlanKey.MemPlus,
    displayName: "Mem Plus",
    shortName: "Plus",
    monthlyPriceCents: 900,
    position: 1,
    supportsPaidExtraUsage: false,
    allowances: buildAllowances({
      userRequests: 50,
      newNotes: 50,
      activeNotes: 50,
      openTasksProjects: 25,
      activeCustomRoutines: 0,
      routineExecutions: 0,
      activeConnections: 0,
      voiceModeHours: 10,
    }),
  },
  {
    key: CommercialPlanKey.MemPro,
    displayName: "Mem Pro",
    shortName: "Pro",
    monthlyPriceCents: 2_900,
    position: 2,
    supportsPaidExtraUsage: false,
    allowances: buildAllowances({
      userRequests: 100,
      newNotes: 100,
      activeNotes: 100,
      openTasksProjects: 50,
      activeCustomRoutines: 1,
      routineExecutions: 50,
      activeConnections: 1,
      voiceModeHours: 25,
    }),
  },
  {
    key: CommercialPlanKey.MemPro2x,
    displayName: "Mem Pro 2×",
    shortName: "Pro 2×",
    monthlyPriceCents: 4_900,
    position: 3,
    supportsPaidExtraUsage: false,
    allowances: buildAllowances({
      userRequests: 200,
      newNotes: 200,
      activeNotes: 200,
      openTasksProjects: 100,
      activeCustomRoutines: 2,
      routineExecutions: 100,
      activeConnections: 2,
      voiceModeHours: 50,
    }),
  },
  {
    key: CommercialPlanKey.MemPro5x,
    displayName: "Mem Pro 5×",
    shortName: "Pro 5×",
    monthlyPriceCents: 9_900,
    position: 4,
    supportsPaidExtraUsage: false,
    allowances: buildAllowances({
      userRequests: 500,
      newNotes: 500,
      activeNotes: 500,
      openTasksProjects: 250,
      activeCustomRoutines: 5,
      routineExecutions: 250,
      activeConnections: 5,
      voiceModeHours: 125,
    }),
  },
  {
    key: CommercialPlanKey.MemPro10x,
    displayName: "Mem Pro 10×",
    shortName: "Pro 10×",
    monthlyPriceCents: 14_900,
    position: 5,
    supportsPaidExtraUsage: false,
    allowances: buildAllowances({
      userRequests: 1_000,
      newNotes: 1_000,
      activeNotes: 1_000,
      openTasksProjects: 500,
      activeCustomRoutines: 10,
      routineExecutions: 500,
      activeConnections: 10,
      voiceModeHours: 250,
    }),
  },
  {
    key: CommercialPlanKey.MemPro20x,
    displayName: "Mem Pro 20×",
    shortName: "Pro 20×",
    monthlyPriceCents: 19_900,
    position: 6,
    supportsPaidExtraUsage: true,
    allowances: buildAllowances({
      userRequests: 2_000,
      newNotes: 2_000,
      activeNotes: 2_000,
      openTasksProjects: 1_000,
      activeCustomRoutines: 20,
      routineExecutions: 1_000,
      activeConnections: 20,
      voiceModeHours: 500,
    }),
  },
] as const;

export const COMMERCIAL_EXTRA_USAGE_BLOCK = {
  key: "mem_pro_extra_usage_monthly_v1",
  displayName: "Extra Usage",
  monthlyPriceCents: 5_000,
  allowances: buildAllowances({
    userRequests: 500,
    newNotes: 500,
    activeNotes: 500,
    openTasksProjects: 250,
    activeCustomRoutines: 5,
    routineExecutions: 250,
    activeConnections: 5,
    voiceModeHours: 125,
  }),
} as const;

const COMMERCIAL_PLAN_BY_KEY = new Map(
  COMMERCIAL_PLAN_CATALOG.map(plan => [plan.key, plan] as const)
);

export const getCommercialPlan = (
  key: string | null | undefined
): CommercialPlanPresentationDefinition | undefined => {
  if (!key) {
    return undefined;
  }

  return COMMERCIAL_PLAN_BY_KEY.get(key as CommercialPlanKey);
};

export const isCommercialPlanKey = (key: string): key is CommercialPlanKey =>
  COMMERCIAL_PLAN_BY_KEY.has(key as CommercialPlanKey);

export const formatCommercialPrice = (monthlyPriceCents: number): string => {
  if (monthlyPriceCents === 0) {
    return "$0";
  }

  return `$${monthlyPriceCents / 100}`;
};
